import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/feature/AdminLayout";
import LoadingState from "@/components/base/LoadingState";
import ErrorState from "@/components/base/ErrorState";
import AdminHeader from "@/components/feature/AdminHeader";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";

/* ─── Types ─────────────────────────────────────────────────── */
interface EmailRow {
  id: string;
  to_email: string;
  subject: string;
  body: string;
  status: string;
  template_type: string;
  entity_type: string | null;
  entity_id: string | null;
  priority: number;
  scheduled_for: string | null;
  created_at: string;
  retry_count: number;
  last_error: string | null;
}

interface WhatsAppRow {
  id: string;
  to_phone: string;
  message_type: string;
  template_name: string | null;
  body: string | null;
  status: string;
  created_at: string;
  sent_at: string | null;
  retry_count: number;
  last_error: string | null;
}

interface AiConversationRow {
  id: string;
  channel: string;
  status: string;
  created_at: string;
  updated_at: string;
  client_id: string | null;
  lead_id: string | null;
  message_count?: number;
}

type TabKey = "email" | "whatsapp" | "ai";

/* ─── Status config ──────────────────────────────────────────── */
const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  pending:    { label: "Pending",    color: "#F59E0B", bg: "rgba(245,158,11,0.12)",  icon: "ri-time-line" },
  sent:       { label: "Sent",       color: "#22C55E", bg: "rgba(34,197,94,0.12)",   icon: "ri-check-double-line" },
  failed:     { label: "Failed",     color: "#EF4444", bg: "rgba(239,68,68,0.12)",   icon: "ri-close-circle-line" },
  processing: { label: "Processing", color: "#6B8FD4", bg: "rgba(107,143,212,0.12)", icon: "ri-loader-4-line" },
  open:       { label: "Open",       color: "#34A853", bg: "rgba(52,168,83,0.12)",   icon: "ri-chat-3-line" },
  closed:     { label: "Closed",     color: "#6B7280", bg: "rgba(107,114,128,0.12)", icon: "ri-check-line" },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? { label: status, color: "#6B7280", bg: "rgba(107,114,128,0.12)", icon: "ri-question-line" };
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap"
      style={{ backgroundColor: cfg.bg, color: cfg.color }}
    >
      <i className={`${cfg.icon} text-[10px]`} />
      {cfg.label}
    </span>
  );
}

/* ─── Retry badge ────────────────────────────────────────────── */
function RetryBadge({ count }: { count: number }) {
  if (count === 0) return <span className="text-slate-300 text-xs">—</span>;
  return (
    <span className={`text-xs font-mono font-semibold ${count >= 3 ? "text-red-400" : "text-amber-400"}`}>
      {count}×
    </span>
  );
}

/* ─── Timestamp ──────────────────────────────────────────────── */
function Ts({ iso }: { iso: string | null }) {
  if (!iso) return <span className="text-slate-300 text-xs">—</span>;
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  return (
    <div>
      <p className="text-slate-500 text-xs">{date}</p>
      <p className="text-slate-400 text-[10px]">{time}</p>
    </div>
  );
}

/* ─── Empty state ────────────────────────────────────────────── */
function EmptyState({ icon, label, description }: { icon: string; label: string; description?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-4 text-center max-w-xs mx-auto">
      <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#C8A96E]/8">
        <i className={`${icon} text-2xl text-[#2563eb]/50`} />
      </div>
      <div>
        <p className="text-slate-400 text-sm font-medium mb-1">{label}</p>
        <p className="text-slate-300 text-xs leading-relaxed">
          {description ?? "Items will appear here once the queue is populated."}
        </p>
      </div>
    </div>
  );
}

/* ─── Page ──────────────────────────────────────────────────── */
export default function IntegrationQueuePage() {
  const navigate = useNavigate();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("email");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const [emailRows, setEmailRows] = useState<EmailRow[]>([]);
  const [waRows, setWaRows]       = useState<WhatsAppRow[]>([]);
  const [aiRows, setAiRows]       = useState<AiConversationRow[]>([]);

  const [expandedError, setExpandedError] = useState<string | null>(null);
  const [expandedBody, setExpandedBody]   = useState<string | null>(null);

  /* ─── Fetch ── */
  const fetchAll = useCallback(async (tid: string, silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(null);

    try {
    const [emailRes, waRes, aiRes, aiMsgRes] = await Promise.all([
      supabase
        .from("email_queue")
        .select("id, to_email, subject, body, status, template_type, entity_type, entity_id, priority, scheduled_for, created_at, retry_count, last_error")
        .eq("tenant_id", tid)
        .order("created_at", { ascending: false })
        .limit(100),

      supabase
        .from("whatsapp_queue")
        .select("id, to_phone, message_type, template_name, body, status, created_at, sent_at, retry_count, last_error")
        .eq("tenant_id", tid)
        .order("created_at", { ascending: false })
        .limit(100),

      supabase
        .from("ai_conversations")
        .select("id, channel, status, created_at, updated_at, client_id, lead_id")
        .eq("tenant_id", tid)
        .order("updated_at", { ascending: false })
        .limit(100),

      supabase
        .from("ai_messages")
        .select("conversation_id")
        .eq("tenant_id", tid),
    ]);

    setEmailRows((emailRes.data as EmailRow[]) ?? []);
    setWaRows((waRes.data as WhatsAppRow[]) ?? []);

    // Attach message counts to conversations
    const msgCounts: Record<string, number> = {};
    ((aiMsgRes.data ?? []) as { conversation_id: string }[]).forEach((m) => {
      msgCounts[m.conversation_id] = (msgCounts[m.conversation_id] ?? 0) + 1;
    });
    const aiWithCount = ((aiRes.data as AiConversationRow[]) ?? []).map((c) => ({
      ...c,
      message_count: msgCounts[c.id] ?? 0,
    }));
    setAiRows(aiWithCount);
    setLoading(false);
    setRefreshing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load queue data.");
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchAll(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchAll]);

  const handleRefresh = () => {
    if (tenantId) fetchAll(tenantId, true);
  };

  /* ─── Tab counts ── */
  const pendingEmail = emailRows.filter((r) => r.status === "pending").length;
  const pendingWa    = waRows.filter((r) => r.status === "pending").length;
  const openAi       = aiRows.filter((r) => r.status === "open").length;

  const tabs: { key: TabKey; label: string; icon: string; color: string; count: number; total: number }[] = [
    { key: "email",    label: "Email Queue",     icon: "ri-mail-send-line",    color: "#6B8FD4", count: pendingEmail, total: emailRows.length },
    { key: "whatsapp", label: "WhatsApp Queue",  icon: "ri-whatsapp-line",     color: "#4DB858", count: pendingWa,    total: waRows.length },
    { key: "ai",       label: "AI Conversations",icon: "ri-robot-line",        color: "#C8A96E", count: openAi,       total: aiRows.length },
  ];

  /* ─── Render ── */
  return (
    <AdminLayout>
      <AdminHeader
        title="Integration Queues"
        subtitle="Monitor pending emails, WhatsApp messages, and AI conversations"
      />

      <div className="px-4 md:px-8 py-4 md:py-7">

        {/* Top bar */}
        <div className="flex items-center justify-between mb-6">
          {/* Summary chips */}
          <div className="flex items-center gap-3">
            {tabs.map((t) => (
              <div
                key={t.key}
                className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-slate-200 bg-white"
              >
                <i className={`${t.icon} text-sm`} style={{ color: t.color }} />
                <div>
                  <p className="text-slate-400 text-[10px] leading-none mb-0.5">{t.label}</p>
                  <p className="text-[#0f172a] text-xs font-semibold">
                    {t.count > 0 && (
                      <span style={{ color: t.color }} className="mr-1">{t.count} pending ·</span>
                    )}
                    <span className="text-slate-400">{t.total} total</span>
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Refresh */}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 text-xs text-slate-400 hover:text-[#0f172a] border border-slate-200 rounded-lg hover:bg-slate-50 transition-all cursor-pointer whitespace-nowrap disabled:opacity-40"
          >
            <i className={`ri-refresh-line text-sm ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-white rounded-xl p-1 border border-slate-200 mb-6 w-fit">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === t.key
                  ? "text-[#0f172a] bg-white/8"
                  : "text-[#0f172a]/35 hover:text-slate-500 hover:bg-white/4"
              }`}
            >
              <i
                className={`${t.icon} text-sm`}
                style={{ color: activeTab === t.key ? t.color : undefined }}
              />
              {t.label}
              {t.count > 0 && (
                <span
                  className="px-1.5 py-0.5 rounded-full text-[10px] font-bold"
                  style={{ backgroundColor: `${t.color}20`, color: t.color }}
                >
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message={error} onRetry={() => tenantId && fetchAll(tenantId)} showLogin />
        ) : (
          <>
            {/* ─── EMAIL QUEUE ─── */}
            {activeTab === "email" && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                {emailRows.length === 0 ? (
                  <EmptyState
                    icon="ri-mail-send-line"
                    label="Email queue is empty"
                    description="Emails are queued here when contracts are signed, appointments scheduled, deliveries prepared, or invitations published. They'll send automatically once the backend is connected."
                  />
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left px-5 py-3.5 text-slate-400 text-xs font-medium">Recipient</th>
                        <th className="text-left px-5 py-3.5 text-slate-400 text-xs font-medium">Subject</th>
                        <th className="text-left px-4 py-3.5 text-slate-400 text-xs font-medium">Type</th>
                        <th className="text-left px-4 py-3.5 text-slate-400 text-xs font-medium">Status</th>
                        <th className="text-left px-4 py-3.5 text-slate-400 text-xs font-medium">Retries</th>
                        <th className="text-left px-4 py-3.5 text-slate-400 text-xs font-medium">Queued</th>
                        <th className="text-left px-4 py-3.5 text-slate-400 text-xs font-medium">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emailRows.map((row) => (
                        <tr key={row.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors">
                          <td className="px-5 py-3.5">
                            <p className="text-[#0f172a]/80 text-xs font-mono">{row.to_email}</p>
                          </td>
                          <td className="px-5 py-3.5">
                            <p className="text-slate-500 text-xs max-w-[220px] truncate">{row.subject}</p>
                            {row.body && (
                              <button
                                onClick={() => setExpandedBody(expandedBody === row.id ? null : row.id)}
                                className="text-slate-300 text-[10px] hover:text-slate-400 transition-colors cursor-pointer mt-0.5"
                              >
                                {expandedBody === row.id ? "Hide body" : "Preview body"}
                              </button>
                            )}
                            {expandedBody === row.id && (
                              <div className="mt-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200 max-h-24 overflow-y-auto">
                                <pre className="text-slate-400 text-[10px] whitespace-pre-wrap font-mono leading-relaxed">
                                  {row.body}
                                </pre>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3.5">
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-medium whitespace-nowrap">
                              {row.template_type}
                            </span>
                            {row.entity_type && (
                              <span className="block text-[10px] text-slate-300 mt-0.5">
                                {row.entity_type}:{row.entity_id?.slice(0, 8)}…
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3.5"><StatusBadge status={row.status} /></td>
                          <td className="px-4 py-3.5"><RetryBadge count={row.retry_count ?? 0} /></td>
                          <td className="px-4 py-3.5"><Ts iso={row.created_at} /></td>
                          <td className="px-4 py-3.5">
                            {row.last_error ? (
                              <div>
                                <button
                                  onClick={() => setExpandedError(expandedError === row.id ? null : row.id)}
                                  className="flex items-center gap-1.5 text-red-400/70 hover:text-red-400 text-[11px] transition-colors cursor-pointer"
                                >
                                  <i className="ri-error-warning-line text-xs" />
                                  {expandedError === row.id ? "Hide" : "View error"}
                                </button>
                                {expandedError === row.id && (
                                  <div className="mt-1.5 px-2.5 py-2 bg-red-50 rounded-lg border border-red-200 max-w-[200px]">
                                    <p className="text-red-500/80 text-[10px] font-mono leading-relaxed break-all">
                                      {row.last_error}
                                    </p>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-300 text-xs">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* ─── WHATSAPP QUEUE ─── */}
            {activeTab === "whatsapp" && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                {waRows.length === 0 ? (
                  <EmptyState icon="ri-whatsapp-line" label="WhatsApp queue is empty" description="WhatsApp messages will appear here once the integration is active and messages are triggered by automations." />
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left px-5 py-3.5 text-slate-400 text-xs font-medium">Phone</th>
                        <th className="text-left px-5 py-3.5 text-slate-400 text-xs font-medium">Type / Template</th>
                        <th className="text-left px-4 py-3.5 text-slate-400 text-xs font-medium">Status</th>
                        <th className="text-left px-4 py-3.5 text-slate-400 text-xs font-medium">Retries</th>
                        <th className="text-left px-4 py-3.5 text-slate-400 text-xs font-medium">Queued</th>
                        <th className="text-left px-4 py-3.5 text-slate-400 text-xs font-medium">Sent</th>
                        <th className="text-left px-4 py-3.5 text-slate-400 text-xs font-medium">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {waRows.map((row) => (
                        <tr key={row.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors">
                          <td className="px-5 py-3.5">
                            <p className="text-[#0f172a]/80 text-xs font-mono">{row.to_phone}</p>
                          </td>
                          <td className="px-5 py-3.5">
                            <p className="text-slate-500 text-xs font-medium capitalize">{row.message_type.replace(/_/g, " ")}</p>
                            {row.template_name && (
                              <p className="text-slate-400 text-[10px] font-mono">{row.template_name}</p>
                            )}
                            {row.body && (
                              <button
                                onClick={() => setExpandedBody(expandedBody === row.id ? null : row.id)}
                                className="text-slate-300 text-[10px] hover:text-slate-400 transition-colors cursor-pointer mt-0.5"
                              >
                                {expandedBody === row.id ? "Hide body" : "Preview body"}
                              </button>
                            )}
                            {expandedBody === row.id && row.body && (
                              <div className="mt-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200 max-h-24 overflow-y-auto">
                                <pre className="text-slate-400 text-[10px] whitespace-pre-wrap font-mono leading-relaxed">
                                  {row.body}
                                </pre>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3.5"><StatusBadge status={row.status} /></td>
                          <td className="px-4 py-3.5"><RetryBadge count={row.retry_count ?? 0} /></td>
                          <td className="px-4 py-3.5"><Ts iso={row.created_at} /></td>
                          <td className="px-4 py-3.5"><Ts iso={row.sent_at} /></td>
                          <td className="px-4 py-3.5">
                            {row.last_error ? (
                              <div>
                                <button
                                  onClick={() => setExpandedError(expandedError === row.id ? null : row.id)}
                                  className="flex items-center gap-1.5 text-red-400/70 hover:text-red-400 text-[11px] transition-colors cursor-pointer"
                                >
                                  <i className="ri-error-warning-line text-xs" />
                                  {expandedError === row.id ? "Hide" : "View error"}
                                </button>
                                {expandedError === row.id && (
                                  <div className="mt-1.5 px-2.5 py-2 bg-red-50 rounded-lg border border-red-200 max-w-[200px]">
                                    <p className="text-red-500/80 text-[10px] font-mono leading-relaxed break-all">
                                      {row.last_error}
                                    </p>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-300 text-xs">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* ─── AI CONVERSATIONS ─── */}
            {activeTab === "ai" && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                {aiRows.length === 0 ? (
                  <EmptyState icon="ri-robot-line" label="No AI conversations yet" description="AI conversations will appear here once the assistant is active and clients start interacting via chat or WhatsApp." />
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left px-5 py-3.5 text-slate-400 text-xs font-medium">ID</th>
                        <th className="text-left px-5 py-3.5 text-slate-400 text-xs font-medium">Channel</th>
                        <th className="text-left px-4 py-3.5 text-slate-400 text-xs font-medium">Status</th>
                        <th className="text-left px-4 py-3.5 text-slate-400 text-xs font-medium">Messages</th>
                        <th className="text-left px-4 py-3.5 text-slate-400 text-xs font-medium">Client / Lead</th>
                        <th className="text-left px-4 py-3.5 text-slate-400 text-xs font-medium">Started</th>
                        <th className="text-left px-4 py-3.5 text-slate-400 text-xs font-medium">Last Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aiRows.map((row) => (
                        <tr key={row.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors">
                          <td className="px-5 py-3.5">
                            <p className="text-slate-400 text-[10px] font-mono">{row.id.slice(0, 8)}…</p>
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-2">
                              <i
                                className={`text-sm ${
                                  row.channel === "whatsapp"
                                    ? "ri-whatsapp-line text-[#4DB858]"
                                    : row.channel === "email"
                                    ? "ri-mail-line text-[#6B8FD4]"
                                    : "ri-global-line text-[#2563eb]"
                                }`}
                              />
                              <span className="text-slate-500 text-xs capitalize">{row.channel}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3.5"><StatusBadge status={row.status} /></td>
                          <td className="px-4 py-3.5">
                            <div className="flex items-center gap-1.5">
                              <i className="ri-chat-3-line text-slate-400 text-xs" />
                              <span className="text-slate-500 text-xs">{row.message_count ?? 0}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3.5">
                            {row.client_id ? (
                              <span className="flex items-center gap-1.5 text-[11px] text-slate-400">
                                <i className="ri-user-line text-xs" />
                                Client linked
                              </span>
                            ) : row.lead_id ? (
                              <span className="flex items-center gap-1.5 text-[11px] text-slate-400">
                                <i className="ri-user-received-line text-xs" />
                                Lead linked
                              </span>
                            ) : (
                              <span className="text-slate-300 text-xs">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3.5"><Ts iso={row.created_at} /></td>
                          <td className="px-4 py-3.5"><Ts iso={row.updated_at} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Footer note */}
            <div className="mt-6 flex items-start gap-3 px-4 py-4 bg-white rounded-xl border border-slate-200">
              <span className="w-5 h-5 flex items-center justify-center text-[#2563eb]/60 mt-0.5 shrink-0">
                <i className="ri-eye-line text-sm" />
              </span>
              <div>
                <p className="text-slate-400 text-xs font-medium mb-0.5">Read-only Monitor</p>
                <p className="text-slate-300 text-xs leading-relaxed">
                  This view shows queued items waiting to be processed by the backend.
                  Items appear here as soon as they are created in the system. Actual
                  sending/processing begins once the backend Edge Functions are connected.
                  Use <span className="font-mono text-slate-500">retry_count</span> and{" "}
                  <span className="font-mono text-slate-500">last_error</span> to debug failed
                  items when the backend is live.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}